/**
 * @typedef {'wheel'|'tire'} RideStylerIntegrationProductType
 */

/**
 * @typedef {'en'|'fr'|{[key:string]:string}} RideStylerIntegrationLanguage
 */

/**
 * @typedef RideStylerIntegrationVehicleMatcher
 * @prop {string} [Make] The make name to match on
 * @prop {string} [Model] The model name to match on
 * @prop {number} [Year]  The year to match on
 * @prop {string} [Trim] The trim name to match on
 */

/**
 * @typedef {RideStylerIntegrationVehicleMatcher & RideStylerSuspensionRange} RideStylerIntegrationSuspensionOverride
 */

/**
 * @typedef RideStylerIntegrationInitializationOptions
 * @prop {string}                                     vehicleDescription          A description of the selected vehicle to display to the user
 * @prop {string}                                     vehicleOESize               The OE size of product that fits on the vehicle
 * @prop {RideStylerIntegrationProduct[]}             products                    The products
 * @prop {string}                                     [productDisclaimer]         Specifies the disclaimer that appears next to the product title.
 * @prop {boolean}                                    [modalOnlyMode]             If true, the visualizer will only be shown when a user clicks on a view on vehicle button, in a modal
 * @prop {boolean}                                    [hideExpand]                If true, hide the Expand/Minimize button
 * @prop {boolean}                                    [hideProductGallery]        If true, hide the product gallery (image carousel) at the bottom of the visualizer
 * @prop {boolean}                                    [hideMoreDetails]           If true, hide the more details button
 * @prop {boolean}                                    [hideSizes]                 If true, hide the UI for choosing a new size
 * @prop {boolean}                                    [hideVehicleDescription]    If true, hide the vehicle description
 * @prop {boolean}                                    [hideButtonText]            If true, hide the text under the action buttons in the visualizer (i.e. expand, change view, color and suspension)
 * @prop {boolean}                                    [moreDetailsRedirects]      If true, the more details button redirects the user to the more details URL instead of opening it in a new window
 * @prop {boolean}                                    [hidePaginationButtons]     If true, the pagination buttons will be hidden
 * @prop {boolean}                                    [enableCompactMode]         If true, a compact mode of the visualizer is enabled
 * @prop {boolean}                                    [hideSuspension]            If true, hide the Suspension button
 * @prop {boolean}                                    [enableCompactPaint]        If true, the paint menu is shown as a compact popover instead of a dialog.
 * @prop {RideStylerIntegrationLanguage}              [language]                  Either the predefined language code to use or a dictionary of translations to use
 * @prop {RideStylerIntegrationSuspensionOverride[]}  [suspensionOverrides]       Allows overriding the suspension ranges for specific vehicles
 * @prop {boolean|number[]}                           [enableTireSizeAdjustments] If true, this enables UI to allow the user to modify the size of the tire being visualized. Optionally you can set this to an array of the tire size diameters to provide.
 * @prop {boolean}                                    [enableSharing]             If true, enables sharing of the current vehicle configuration using RideStyler's API
 */

/**
  * @typedef RideStylerIntegrationVehicleSelection
  * @prop {string}  vehicleDescription A description of the selected vehicle to display to the user
  * @prop {string}  vehicleOESize      The OE size of product that fits on the vehicle
  */
 
/**
 * @typedef RideStylerIntegrationProductGroup
 * @prop {string}                           title           A title for the product groupo
 * @prop {string}                           style           A style/finish for the product group
 * @prop {RideStylerIntegrationProductType} type            The type of product
 * @prop {string}                           imageURL        The URL of the image to display for this group in the gallery
 * @prop {string}                           brandLogoURL    The URL for a logo for this product's brand, displayed next to the product's title
 * @prop {RideStylerIntegrationProduct[]}   products        The products in this group
 */

/**
 * @typedef RideStylerIntegrationProduct
 * @prop {string}                           title            A title for the product
 * @prop {string}                           style            A style/finish for the product
 * @prop {RideStylerIntegrationProductType} [type='wheel']   The type of product
 * @prop {string}                           fullDescription  A full description for this product fitment
 * @prop {string}                           moreDetailsURL   The URL to open when the user clicks the more details button with this product selected
 * @prop {string}                           imageURL         The URL of the image to display for this product in the gallery
 * @prop {string}                           partNumber       The part-number/SKU for this product
 * @prop {string}                           size             The size to display for this product
 * @prop {string}                           brandLogoURL     The URL for a logo for this product's brand, displayed next to the product's title
 * @prop {boolean}                          renderable       Whether or not this product is renderable on the currently selected vehicle
 * @prop {boolean}                          hasSideImage     Whether or not this product has a side image
 * @prop {boolean}                          hasAngledImage   Whether or not this product has a angled image
 */

/**
 * @typedef RideStylerIntegrationErrorOverlaySettings
 * @prop {string} key
 * @prop {string} header
 * @prop {string} text
 * @prop {boolean} showRefresh
 * @prop {boolean} showClose
 */

/** 
 * @typedef RideStylerSuspensionRange
 * @prop {number} Min
 * @prop {number} Max
 */

/**
 * Initialize the RideStyler Integration
 * @param {JQuery|Element|string} containerSelector
 * @param {RideStylerIntegrationInitializationOptions} options
 */
var ridestylerIntegration = function (containerSelector, options) {
    var language = (function () {
        var language = {
            'view-in-visualizer'                                  : 'View in Visualizer',
            'more-details'                                        : 'More Details',
            'vehicle-paint-color'                                 : 'Vehicle Paint Color',
            'change-trim'                                         : 'Change Trim',
            'apply-changes'                                       : 'Apply Changes',
            'expand'                                              : 'Expand',
            'change-view'                                         : 'Change View',
            'color'                                               : 'Color',
            'suspension'                                          : 'Suspension',
            'message-trim-more-options'                           : 'We noticed the vehicle selection comes with a few different options!',
            'message-select-trim'                                 : 'Please select a Trim option from the choices below:',
            'message-rotate-phone'                                : 'Rotate your phone to landscape to use the visualizer',
            'title-no-vehicle-image'                              : 'We couldn\'t find an image for that vehicle.',
            'message-no-vehicle-image'                            : 'The Visualizer couldn\'t locate an image of your vehicle. Check back later - new images are added all the time!',
            'title-generic-error'                                 : 'Whoops! Something didn\'t load quite right!',
            'message-generic-error'                               : 'The Visualizer had issues mounting that particular product. Please refresh your page and try again.',
            'refresh'                                             : 'Refresh',
            'exit-visualizer'                                     : 'Exit Visualizer',
            'no-preview-available'                                : 'No Preview Available',
            'title-issue-visualizing-error'                       : 'Whoops! There was an issue visualizing the product you selected',
            'message-issue-visualizing-error'                     : 'The selected product cannot be visualized on your vehicle',
            'previous'                                            : "Previous",
            'next'                                                : "Next",
            'tires'                                               : "Tires",
            'tire-size'                                           : "Tire Size",
            'message-tire-size-adjustments-unavailable-angle'     : 'Tire size adjustments are unavailable for this angle.',
            'message-tire-size-adjustments-change-to-side-view'   : 'Change to the side view for this vehicle to adjust tire size.',
            'message-tire-size-adjustments-unavailable-needs-lift': 'Please apply at least a 2″ lift to the vehicle to adjust tire size.',
            'tire-size-adjustments-warning'                       : 'Tire size adjustments are for visualization purposes only',
            'tire-size-adjustment-disclaimer'                     : 'This tool is for visualization purposes only. We do not guarantee the fit, sizing, overall appearance, or any combination of the selected products, as depicted on any vehicle.',
            'click-here-to-accept'                                : 'Click Here to Accept',
            'share'                                               : 'Share',
            'important-note'                                      : 'Important Note',
            'close'                                               : 'Close'
        };
        
        if (typeof options.language === 'object') {
            for (var key in language)
            if (key in options.language === false) 
            options.language[key] = key;
            
            language = options.language;
        } else if (options.language === 'fr') {
            language = {
                'view-in-visualizer'                                  : 'Vue en visualiseur',
                'more-details'                                        : 'plus de détails',
                'vehicle-paint-color'                                 : 'couleur de peinture du véhicule',
                'change-trim'                                         : 'changer de garniture',
                'apply-changes'                                       : 'appliquer les modifications',
                'expand'                                              : 'Etendre',
                'change-view'                                         : 'Changer la vue',
                'color'                                               : 'Couleur',
                'suspension'                                          : 'Suspension',
                'message-trim-more-options'                           : 'Nous avons remarqué que la sélection du véhicules a quelques options différentes!',
                'message-select-trim'                                 : 'Veuillez sélectionner une option parmi les choix ci-dessous.',
                'message-rotate-phone'                                : 'Pivoter votre téléphone en mode paysage pour utiliser le visualiseur',
                'title-no-vehicle-image'                              : 'Nous n\'avons pas trouver d\'image pour ce véhicule.',
                'message-no-vehicle-image'                            : 'Le visualiseur n\'a pas pu localiser une image de votre véhicule. Revenez plus tard - de nouvelles images sont ajoutées tout le temps!',
                'title-generic-error'                                 : 'Whoops! Quelque chose ne s’est pas charger correctement',
                'message-generic-error'                               : 'Le Visualiseur avait des problèmes de montage de ce produit en particulier. Veuillez actualiser votre page et réessayer.',
                'refresh'                                             : 'Actualiser',
                'exit-visualizer'                                     : 'Sortir de visualiseur',
                'no-preview-available'                                : 'Aucun aperçu disponible',
                'title-issue-visualizing-error'                       : 'Whoops! Un problème est survenu lors de la visualisation du produit sélectionné.',
                'message-issue-visualizing-error'                     : 'Le produit sélectionné ne peut pas être visualisé sur votre véhicule',
                'previous'                                            : 'Précédent',
                'next'                                                : 'Suivant',
                'tires'                                               : 'Pneus',
                'tire-size'                                           : 'Taille des Pneus',
                'message-tire-size-adjustments-unavailable-angle'     : 'Les réglages de la taille des pneus ne sont pas disponibles pour cet angle.',
                'message-tire-size-adjustments-change-to-side-view'   : 'Changez la vue de côté de ce véhicule pour ajuster la taille des pneus.',
                'message-tire-size-adjustments-unavailable-needs-lift': 'Veuillez appliquer au moins un soulèvement de 2″ po au véhicule pour ajuster la taille du pneu.',
                'tire-size-adjustments-warning'                       : 'Les réglages de la taille des pneus sont uniquement à des fins de visualisation',
                'tire-size-adjustment-disclaimer'                     : 'Cet outil est uniquement à des fins de visualisation. Nous ne garantissons pas l\'ajustement, le dimensionnement, l\'apparence générale ou toute combinaison des produits sélectionnés, tels qu\'ils sont représentés sur un véhicule.',
                'click-here-to-accept'                                : 'Cliquez ici pour accepter',
                'share'                                               : 'Partager',
                'important-note'                                      : 'Note Importante',
                'close'                                               : 'Ferme'
            };
        }

        return language;
    })();
    var defaultSuspensionOverrides = [
        {
            Make: "Subaru",
            Model: "CrossTrek",
            Min: -4,
            Max: 4
        },
        {
            Make: "Subaru",
            Model: "Outback",
            Min: -4,
            Max: 4
        },
        {
            Make: "Subaru",
            Model: "Forester",
            Min: -4,
            Max: 4
        },
        {
            Make: "Subaru",
            Model: "Ascent",
            Min: -4,
            Max: 4
        }
    ];

    var templateHtml = window.rideStylerIntegrationTemplates["template.html"].replace(/{%([A-Za-z0-9-_]+)%}/g, function (_match, languageKey) {
                if (languageKey in language === false) {
                    console.warn('Missing "' + languageKey + '" from language "' + language + '"');
                    return languageKey;
                }

                return language[languageKey];
            });

    /** @type {JQuery} */
    var $container = $(containerSelector).first().addClass('rs-initializing').html(templateHtml);

    var DEBUG_EVENTS = false;

    var TIRE_SIZE_ADJUSTMENTS_MIN_VEHICLE_LIFT = 2;

    var EVENT_STATE_EXIT = 'state-exit';
    var EVENT_STATE_ENTER = 'state-enter';
    var EVENT_PRODUCT_SELECTED = 'product-selected';
    var EVENT_PRODUCT_GROUP_SELECTED = 'product-group-selected';
    var EVENT_PRODUCTS_INITIALIZED = 'products-initialized';
    var EVENT_PRODUCTS_UPDATED = 'products-updated';
    var EVENT_RENDER_STATUS_LOADED = 'render-status-loaded';
    var EVENT_VEHICLE_INITIALIZED = 'vehicle-initialized';
    var EVENT_AFTER_VEHICLE_INITIALIZED = 'after-vehicle-initialized';
    var EVENT_EXPANDED_CONTRACTED = 'view-expand-contract';
    var EVENT_SUSPENSION_CHANGED = 'suspension-changed';
    var EVENT_PAINT_CHANGED = 'paint-changed';
    var EVENT_TIRE_DIAMETER_CHANGED = 'tire-diameter-changed';
    var EVENT_VEHICLE_VIEW_CHANGED = 'vehicle-view-changed';
    var EVENT_VISUALIZER_MESSAGES_CHANGED = 'visualizer-messages-changes';
    var EVENT_DISCLAIMER_ACCEPTED = 'disclaimer-accepted';

    var _registeredEvents = {};

    var event = {
        /**
         * @param {string} eventName 
         * @param {any} data 
         */
        broadcast: function (eventName, data) {
            if (DEBUG_EVENTS) console.log(eventName, data);

            if (eventName.substr(0, 3) !== 'rs-') eventName = 'rs-' + eventName;
            
            $(document.body).triggerHandler(eventName, [data]);

            if (eventName in _registeredEvents) {
                for (var i = 0; i < _registeredEvents[eventName].length; i++) {
                    var eventHandler = _registeredEvents[eventName][i];
                    
                    eventHandler(data);
                }
            }
        },

        /**
         * 
         * @param {string} eventName 
         * @param {(data)=>void} callback 
         */
        listen: function (eventName, callback) {
            if (eventName.substr(0, 3) !== 'rs-') eventName = 'rs-' + eventName;
            
            if (eventName in _registeredEvents === false) _registeredEvents[eventName] = [];

            _registeredEvents[eventName].push(callback);
        },

        clearListeners: function () {
            $(document.body).off('.rs-visualizer');
            $(document).off('.slider');
        },

        /**
         * @param {string} stateName 
         * @param {()=>void} callback 
         */
        listenStateEnter: function (stateName, callback) {
            this.listen(EVENT_STATE_ENTER, function (enteredStateName) { if (enteredStateName === stateName) callback(); });
        },

        /**
         * @param {string} stateName 
         * @param {()=>void} callback 
         */
        listenStateExit: function (stateName, callback) {
            this.listen(EVENT_STATE_EXIT, function (exitedStateName) { if (exitedStateName === stateName) callback(); });
        }
    };

    var helpers = {
        /**
         * @param {RideStylerIntegrationProductGroup} a 
         * @param {RideStylerIntegrationProductGroup} b 
         */
        compareProductGroups: function (a, b) {
            return a.title === b.title && a.style === b.style && a.type === b.type;
        },

        /**
         * @param {RideStylerIntegrationProductGroup} productGroup 
         */
        productGroupIsRenderable: function (productGroup) {
            for (var i = 0; i < productGroup.products.length; i++) {
                var product = productGroup.products[i];
                
                if (product.renderable) return true;
            }

            return false;
        },
        /**
         * Returns a new function to debounce (https://davidwalsh.name/javascript-debounce-function)  
         * @param {Function} fn The function to call
         * @param {number} wait The amount of time to wait
         */
        debounce: function (fn, wait) {
            var timeout;

            return function () {
                var self = this;
                var args = arguments;
                
                clearTimeout(timeout);
                
                timeout = setTimeout(function () {
                    fn.call(self, args)
                }, wait || 0);
            };
        },

        /**
         * Shows a viewport disclaimer
         * @param {string} selector A selector for selecting the disclaimer element
         * @param {boolean} firstTimeOnly If true, only show this disclaimer once per user
         */
        showViewportDisclaimer: function (selector, firstTimeOnly) {
            if (firstTimeOnly && state.viewportDisclaimerAccepted(selector)) return; // already shown
            
            $(selector, $container).css('display', ''); // show the disclaimer
        },

        /**
         * @param {JQuery<HTMLImageElement>} $img 
         * @param {boolean} [restore] 
         */
        replaceWithLoader: function ($img, restore) {
            var loaderUrl = 'https://static.ridestyler.net/images/loaders/loader_radial_chaser_back_on_white_32.gif';

            if (restore) {
                $img.prop('src', $img.data('original-src'));

                return;
            }

            var imgSrc = $img.prop('src');

            if (imgSrc === loaderUrl) return;

            $img
                .data('original-src', imgSrc)
                .prop('src', loaderUrl);
        }
    };

    var elementFactory = {
        /**
         * Creates an element for a product group in the gallery
         * @param {RideStylerIntegrationProductGroup} productGroup The product group
         */
        createGalleryProduct: function (productGroup) {
            return $('<div/>', {
                "class": "rs-product-gallery-product",
                "append": [
                    $("<img/>", {
                        "src": productGroup.imageURL,
                        "alt": ""
                    }),
                    $('<div/>', {
                        "class": "rs-product-gallery-product-info",
                        "append": $('<div/>', {
                            "class": "rs-product-gallery-product-info-inner",
                            "append": [
                                $('<div/>', {
                                    "class": "rs-product-gallery-product-title",
                                    "text": productGroup.title
                                }),
                                $('<div/>', {
                                    "class": "rs-product-gallery-product-subtitle",
                                    "text": productGroup.style
                                })
                            ]
                        })
                    })
                ],
                'data': {
                    'productGroup': productGroup
                }
            });
        },

        /**
         * Creates an element for a size button representing a product
         * @param {RideStylerIntegrationProduct} product The product
         */
        createSizeButton: function (product) {
            var button = document.createElement('button');
            var oeSize = state.vehicleOESize;
            var size = product.size;

            button.appendChild(document.createTextNode(size === oeSize ? 'OE' : size));

            $.data(button, 'product', product);

            return button;
        },

        /**
         * Creates a button representing a tire size
         * @param {number} tireSize The tire size
         */
        createTireSizeButton: function (tireSize) {
            var button = document.createElement('button');
            
            button.appendChild(document.createTextNode(tireSize + '″'));

            $.data(button, 'tireSize', tireSize);

            return button;
        },
        
        /**
         * @param {ridestyler.Descriptions.VehicleDescriptionModel[]} vehicles 
         */
        createVehicleSelectionButtons: function (vehicles) {
            return $($.map(vehicles, function (vehicle) {
                var button = document.createElement('button');

                button.appendChild(document.createTextNode(vehicle.ModelName + ' ' + vehicle.TrimDescription));

                $.data(button, 'vehicleConfigurationID', vehicle.ConfigurationID);

                return button;
            }));
        },

        /**
         * @param {ridestyler.Descriptions.VehiclePaintSchemeDescriptionModel} paintScheme 
         */
        createPaintSwatch: function (paintScheme) {
            var paintColor = paintScheme.Colors[0].Hex;
            var active = paintColor === state.paintColor;

            var swatch = document.createElement('span');
                swatch.className = 'rs-paint-swatch' + (active ? ' active' : '');
                swatch.style.backgroundColor = swatch.style.color = paintColor;

            var swatchText = document.createTextNode(paintScheme.SchemeName);
                swatch.appendChild(swatchText);

            $.data(swatch, 'color', paintColor);
            
            return swatch;
        },

        createVisualizerMessage: function (message) {
            var el = document.createElement('div');
            
            el.appendChild(document.createTextNode(message.message));

            return el;
        }
    };

    var settings = {
        /** @type {{[key:string]:RideStylerSuspensionRange}} */
        suspensionLimits: {
            Default: {
                Min: -4,
                Max: 0
            },
            Van4WD: {
                Min: -4,
                Max: 4
            },
            Truck4WD: {
                Min: -4,
                Max: 4
            }
        }
    };
    
    var STATE_INITIALIZING = 'initializing';
    var STATE_SELECT_VEHICLE = 'select-vehicle';
    var STATE_VISUALIZE = 'visualize';
    var STATE_NO_VEHICLE = 'no-vehicle';

    var state = {
        current: STATE_INITIALIZING,

        /** @type {RideStylerViewport} */
        visualizer: undefined,

        /**
         * The OE size for this vehicle to compare against products
         * @type {string}
         */
        vehicleOESize: undefined,

        /**
         * The selected VehicleConfigurationID
         * @type {string}
         */
        vehicleConfigurationID: undefined,
        
        /**
         * The current set of grouped products
         * @type {RideStylerIntegrationProductGroup[]}
         */
        productGroups: undefined,

        /**
         * The currently selected product group
         * @type {RideStylerIntegrationProductGroup}
         */
        activeProductGroup: undefined,

        /**
         * The currently selected product
         * @type {RideStylerIntegrationProduct}
         */
        activeProduct: undefined,

        /**
         * The current set of selectable vehicle options
         * @type {ridestyler.Descriptions.VehicleDescriptionModel[]}
         */
        vehicleOptions: undefined,

        /**
         * Whether or not we're visualizing the side view of the car
         */
        visualizingSideView: true,

        /**
         * The last size that was manually selected by the user
         * @type {string}
         */
        lastUserSelectedSize: undefined,

        /**
         * The user's selected vehicle paint color
         */
        paintColor: undefined,

        /**
         * The tire diameter to visualize the vehicle with
         */
        tireDiameter: undefined,

        /**
         * The user's selected vehicle suspension
         */
        suspension: 0,

        /**
         * The current allowed suspension range
         * @type {RideStylerSuspensionRange}
         */
        suspensionRange: undefined,

        /**
         * @type {RideStylerIntegrationErrorOverlaySettings}
         */
        errorOverlay: undefined,

        /**
         * The current status of the products renderability for specific vehicles
         * @type {ridestyler.Responses.VehicleCanBeRenderedResultModel}
         */
        renderability: undefined,

        /**
         * Current messages visible in the visualizer
         * @type {{[key:string]:{message:string, priority: number}}}
         */
        visualizerMessages: {},

        /**
         * A list of disclaimers visible in the viewport already shown to the user
         * @type {string[]}
         */
        acceptedViewportDisclaimers: [],

        /**
         * @param {RideStylerIntegrationErrorOverlaySettings} errorSettings 
         */
        showError: function (errorSettings) {
            this.errorOverlay = errorSettings = errorSettings || {
                key: undefined,
                header: language["title-generic-error"],
                text: language["message-generic-error"],
                showRefresh: true,
                showClose: false
            };
            
            var hideButtons = !this.errorOverlay.showRefresh && !this.errorOverlay.showClose;
            
            $('.rs-error-overlay', $container)
                .children('.rs-overlay-header').text(errorSettings.header).end()
                .children('.rs-overlay-text').text(errorSettings.text).end()
                .children('.rs-overlay-buttons')[hideButtons ? 'hide' : 'show']()
                    .children('#rs-error-refresh-button')[this.errorOverlay.showRefresh ? 'show' : 'hide']().end()
                    .children('#rs-error-close-button')[this.errorOverlay.showClose ? 'show' : 'hide']().end()
                    .end()
                .addClass('show');
        },

        /**
         * @param {string} [key]
         */
        hideError: function (key) {
            if (!key || this.errorOverlay && this.errorOverlay.key === key) 
                $('.rs-error-overlay', $container).removeClass('show');
        },

        change: function (newState) {
            if (newState === this.current) return;

            event.broadcast(EVENT_STATE_EXIT, this.current);
            event.broadcast(EVENT_STATE_ENTER, newState);

            this.current = newState
            this.save();
        },

        /**
         * Saves state data to local storage
         */
        save: function () {
            localStorage.ridestylerIntegrationState = JSON.stringify({
                activeProduct: this.activeProduct && this.activeProduct.partNumber,
                vehicleConfigurationID: this.vehicleConfigurationID,
                suspension: this.suspension,
                paintColor: this.paintColor,
                lastUserSelectedSize: this.lastUserSelectedSize,
                visualizingSideView: this.visualizingSideView,
                tireDiameter: this.tireDiameter,
                acceptedViewportDisclaimers: this.acceptedViewportDisclaimers
            });
        },

        /**
         * Loads state data from local storage
         */
        load: function () {
            return localStorage.ridestylerIntegrationState && JSON.parse(localStorage.ridestylerIntegrationState) || {};
        },

        loadProductRenderabilityStatus: function () {
            var deferred = $.Deferred();

            ridestyler.ajax.send({
                action: 'vehicle/canberendered',
                data: {
                    Vehicles: $.map(this.vehicleOptions, function (vehicleDescription) {
                        return vehicleDescription.ConfigurationID;
                    }),
                    Wheels: $.map(this.productGroups, function (productGroup) {
                        return $.map(productGroup.products, function (product) {
                            return {
                                PartNumber: product.partNumber
                            };
                        });
                    })
                },
                callback: function (/** @type {ridestyler.Responses.VehicleCanBeRenderedResponseModel} */ response) {
                    if (!response.Success) return deferred.reject();

                    state.renderability = response.Result;
                    state.populateProductRenderabilityStatus();

                    deferred.resolve();
                }
            });

            return deferred;
        },

        populateProductRenderabilityStatus: function () {
            if (!this.vehicleConfigurationID) return;

            var renderability = this.renderability[this.vehicleConfigurationID] || {};
            var productGroups = this.productGroups;

            var unrenderableProducts = {};

            $.each(productGroups, function (_i, productGroup) {
                $.each(productGroup.products, function (_j, product) {
                    var partNumber = product.partNumber;
                    var renderable = false;
                    var hasSideImage = false;
                    var hasAngledImage = false;

                    if (partNumber in renderability) {
                        renderable = renderability[partNumber].Renderable;
                        hasSideImage = renderability[partNumber].Side;
                        hasAngledImage = renderability[partNumber].Angled;
                    }

                    product.renderable = renderable;
                    product.hasAngledImage = hasAngledImage;
                    product.hasSideImage = hasSideImage;

                    if (state.productIsUnrenderable(product)) {
                        unrenderableProducts[product.partNumber] = true;
                    }
                });
            });

            $('.rs-visualizer-button').each(function () {
                var $button = $(this);
                var partNumber = $button.data('part-number');
                var unrenderable = partNumber in unrenderableProducts && unrenderableProducts[partNumber];
                var renderable = partNumber in renderability && renderability[partNumber].Renderable;

                if (unrenderable) $button.css('visibility', 'hidden');
                else $button.addClass(renderable ? 'visualizable' : 'not-visualizable');
            });

            event.broadcast(EVENT_PRODUCTS_UPDATED, productGroups);
            event.broadcast(EVENT_RENDER_STATUS_LOADED, renderability);
        },

        /**
         * @param {RideStylerIntegrationProduct} product 
         */
        productIsUnrenderable: function (product) {
            var partNumber = product.partNumber;

            for (var i = 0; i < state.vehicleOptions.length; i++) {
                var vehicleConfigurationID = state.vehicleOptions[i].ConfigurationID;
                var renderability = this.renderability[vehicleConfigurationID] || {};

                if (partNumber in renderability && renderability[partNumber].Renderable) {
                    return false;
                }
            }

            return true;
        },

        tireSizeAdjustments: {
            enabled: function () {
                return options.enableTireSizeAdjustments && state.suspensionRange.Max >= TIRE_SIZE_ADJUSTMENTS_MIN_VEHICLE_LIFT;
            },
            active: function () {
                return state.tireSizeAdjustments.enabled() && state.tireDiameter && state.suspension >= TIRE_SIZE_ADJUSTMENTS_MIN_VEHICLE_LIFT
            }
        },
        
        /**
         * @param {RideStylerIntegrationVehicleSelection} vehicleSelection 
         */
        selectVehicle: function (vehicleSelection) {
            /** @type {JQueryDeferred<ridestyler.Descriptions.VehicleDescriptionModel[]>} */
            var deferred = $.Deferred();

            state.vehicleOESize = vehicleSelection.vehicleOESize;

            ridestyler.ajax.send({
                action: 'vehicle/getdescriptions',
                data: {
                    Search: vehicleSelection.vehicleDescription,
                    SearchOptions: 79
                },
                callback: function (response) {
                    var vehicleOptions = $.map(response.Descriptions, function (vehicle) {
                        if (!vehicle.HasAngledImage && !vehicle.HasSideImage) return undefined;
                        return vehicle;
                    });

                    if (!response.Success || !vehicleOptions) {
                        deferred.reject();
                        state.change(STATE_NO_VEHICLE);
                        return;
                    }

                    if (!vehicleOptions.length) {
                        deferred.reject();
                        state.change(STATE_NO_VEHICLE);
                    } else {
                        state.vehicleOptions = vehicleOptions;
                        state.save();

                        deferred.resolve(vehicleOptions);
                    }

                }
            });

            return deferred;
        },

        /**
         * @param {string} vehicleConfigurationID 
         */
        setVehicle: function (vehicleConfigurationID) {
            state.vehicleConfigurationID = vehicleConfigurationID;
            event.broadcast(EVENT_VEHICLE_INITIALIZED, vehicleConfigurationID);
            event.broadcast(EVENT_AFTER_VEHICLE_INITIALIZED);
            state.change(STATE_VISUALIZE);
        },

        /**
         * @param {boolean} [setSideView=undefined]
         */
        toggleVehicleView: function (setSideView) {
            var visualizeSideView;

            if (typeof setSideView === 'undefined') {
                visualizeSideView = !state.visualizingSideView;
            } else {
                visualizeSideView = setSideView;
            }

            if (state.visualizingSideView == visualizeSideView) return;

            state.visualizingSideView = visualizeSideView;
            state.save();

            event.broadcast(EVENT_VEHICLE_VIEW_CHANGED, visualizeSideView);
        },

        /**
         * @param {RideStylerIntegrationProduct[]} products 
         */
        setProducts: function (products) {
            /** @type {RideStylerIntegrationProductGroup[]} */
            var productGroups = [];

            /** @type {string[]} */
            var partNumbers = [];

            // Build the productGroups list
            {
                /** @type {{[key:string]:RideStylerIntegrationProductGroup}} */
                var productGroupHashSet = {};

                for (var i = 0; i < products.length; i++) {
                    var product = products[i];
                    var type = product.type || 'wheel';
                    var key = (product.title + product.style + type).toLowerCase();

                    if (key in productGroupHashSet) {
                        // If there's a group for the product already, add it to it
                        productGroupHashSet[key].products.push(product);
                    } else {
                        // Otherwise create a new group for the product
                        var productGroup = productGroupHashSet[key] = {
                            title: product.title,
                            style: product.style,
                            type: type,
                            imageURL: product.imageURL,
                            brandLogoURL: product.brandLogoURL,
                            products: [
                                product
                            ]
                        };

                        productGroups.push(productGroup);
                    }

                    // Add the part number to our list of partnumbers to lookup renderability
                    partNumbers.push(product.partNumber);
                }
            }

            state.productGroups = productGroups;

            event.broadcast(EVENT_PRODUCTS_INITIALIZED, productGroups);
            event.broadcast(EVENT_PRODUCTS_UPDATED, productGroups);
        },

        /**
         * @param {RideStylerIntegrationProductGroup} productGroup 
         * @param {RideStylerIntegrationProduct} [product]
         */
        selectProductGroup: function (productGroup, product) {
            this.activeProductGroup = productGroup;

            event.broadcast(EVENT_PRODUCT_GROUP_SELECTED, productGroup);

            if (product) {
                state.selectProduct(product);
                return;
            }

            var oeSize = state.vehicleOESize;
            var lastUserSelectedSize = state.lastUserSelectedSize;

            var oeSizeProduct;
            var lastSelectedSizeProduct;

            for (var i = 0; i < productGroup.products.length; i++) {
                var product = productGroup.products[i];
                var productSize = product.size;
                
                if (productSize === oeSize) oeSizeProduct = product;
                if (productSize === lastUserSelectedSize) lastSelectedSizeProduct = product;
            }

            if (lastSelectedSizeProduct) state.selectProduct(lastSelectedSizeProduct);
            else if (oeSizeProduct) state.selectProduct(oeSizeProduct)
            else state.selectProduct(productGroup.products[0]);
        },

        /**
         * @param {RideStylerIntegrationProduct} product 
         */
        selectProduct: function (product) {
            this.activeProduct = product;

            var visualizingSideView = this.visualizingSideView;
            var visualizingAngledView = !visualizingSideView;

            if (!product.hasAngledImage && visualizingAngledView || !product.hasSideImage && visualizingSideView) 
                this.toggleVehicleView();

            event.broadcast(EVENT_PRODUCT_SELECTED, product);
            this.save();
        },

        /**
         * @param {string} partNumber The part number to select
         * @returns {boolean} True, if a product was selected, otherwise false
         */
        selectPartNumber: function (partNumber) {
            var productGroups = this.validProductGroups();

            for (var i = 0; i < productGroups.length; i++) {
                var productGroup = productGroups[i];
                var products = productGroup.products;

                for (var j = 0; j < products.length; j++) {
                    var product = products[j];
                    
                    if (product.partNumber !== partNumber) continue;

                    this.selectProductGroup(productGroup, product);
                    return true;
                }
            }

            return false;
        },

        validProductGroups: function () {
            return $.map(this.productGroups, function (productGroup) {
                return helpers.productGroupIsRenderable(productGroup) ? productGroup : undefined;
            });
        },

        selectNextProductGroup: function () {
            var productGroups = this.validProductGroups();
            var index = $.inArray(this.activeProductGroup, productGroups);

            if (++index >= productGroups.length || index < 0) index = 0;

            this.selectProductGroup(productGroups[index]);
        },

        selectPreviousProductGroup: function () {
            var productGroups = this.validProductGroups();
            var index = $.inArray(this.activeProductGroup, productGroups);

            if (--index < 0) index = productGroups.length - 1;

            this.selectProductGroup(productGroups[index]);
        },

        /**
         * @returns {ridestyler.Descriptions.VehicleDescriptionModel}
         */
        getCurrentVehicleOption: function () {
            for (var i = 0; i < this.vehicleOptions.length; i++) {
                var vehicleOption = this.vehicleOptions[i];
                
                if (this.vehicleConfigurationID === vehicleOption.ConfigurationID) 
                    return vehicleOption;
            }

            return undefined;
        },

        setSuspension: function (suspension) {
            this.suspension = suspension;
            this.save();

            event.broadcast(EVENT_SUSPENSION_CHANGED, suspension);
        },

        setPaintColor: function (paintColor) {
            this.paintColor = paintColor;
            this.save();

            event.broadcast(EVENT_PAINT_CHANGED, paintColor);
        },

        setTireDiameter: function (tireDiameter) {
            this.tireDiameter = tireDiameter;
            this.save();

            event.broadcast(EVENT_TIRE_DIAMETER_CHANGED, tireDiameter);
        },

        setVisualizerMessage: function (key, message) {
            if (typeof message === 'string') message = { message: message };

            if (!message || !message.message) delete this.visualizerMessages[key];
            else {
                if (typeof message.priority !== 'number') message.priority = 0;
                
                this.visualizerMessages[key] = message;
            }
            
            
            var messages = [];

            for (var key in this.visualizerMessages)
                messages.push(this.visualizerMessages[key]);

            messages.sort(function (a, b) {
                return b.priority - a.priority;
            });
            
            event.broadcast(EVENT_VISUALIZER_MESSAGES_CHANGED, messages);
        },

        /**
         * @param {string} selector The selector used to identify the disclaimer
         * @param {boolean} value If specified, the function will set the value in the state, otherwise it will retrieve it from the state
         */
        viewportDisclaimerAccepted: function (selector, value) {
            var idx = state.acceptedViewportDisclaimers.indexOf(selector);

            if (typeof value !== 'boolean') return idx >= 0;

            if (value) {
                if (idx === -1) {
                    state.acceptedViewportDisclaimers.push(selector);
                    event.broadcast(EVENT_DISCLAIMER_ACCEPTED, selector);
                }
            } else {
                if (idx >= 0) state.acceptedViewportDisclaimers.splice(idx, 1); 
            }
        },

        /** @type {JQueryXHR} */
        currentShareRequest: undefined,

        share: function () {
            if (state.currentShareRequest) {
                state.currentShareRequest.abort();
                state.currentShareRequest = undefined;
            }

            /** @type {ridestyler.Requests.ShareVehicleRequest} */
            var shareOptions = {
                Mode: "Link"
            };

            var $shareIcon = $container.find('#rs-share .rs-icon');

            helpers.replaceWithLoader($shareIcon);

            state.currentShareRequest = ridestyler.ajax.send({
                action: 'share/vehicle',
                data: $.extend(shareOptions, state.visualizer.CurrentInstructions()),
                callback: function(response) {
                    if (response.Success) {
                        window.open(response.Url, "_blank");
                    }

                    helpers.replaceWithLoader($shareIcon, true);
                }
            });

        }
    };

    var initialization = {
        run: function () {
            event.clearListeners();

            this.setupEverythingElse();
            this.setupGallery();
            this.setupHeader();
            this.setupPaint();
            this.setupTireSizeAdjustments();
            this.setupSizeButtons();
            this.setupSuspension();
            this.setupVehicleSelect();
            this.setupViewOnVehicleButtons();
            this.setupVisualizer();
            this.setupVisualizerButtons();

            this.setupResponsiveness();

            var previousState = state.load();

            var settingsLoaded = $.Deferred();

            // Load our organization settings from the server
            ridestyler.ajax.send({
                action: 'Organization/GetSettings',
                data: {
                    Keys: [
                        'SUSPENSION_RANGES'
                    ]
                },
                callback: function (response) {
                    if (response.Success) {
                        // Load up our suspension ranges for this client
                        if (response.Settings['SUSPENSION_RANGES']) {
                            var ranges = JSON.parse(response.Settings['SUSPENSION_RANGES']);
                            $.extend(settings.suspensionLimits, ranges);
                        }

                        settingsLoaded.resolve();
                    }
               } 
            });

            var vehicleOptionsLoaded = state.selectVehicle(options);
            var vehicleSelected = $.Deferred();
            var productSelected = $.Deferred();
            var renderabilityLoaded = $.Deferred();
            var ready = $.when(vehicleSelected, productSelected, renderabilityLoaded);
            
            var checkVehicleProductMount = function () {
                var vehicleConfigurationID = state.vehicleConfigurationID;
                var product = state.activeProduct;

                if (!vehicleConfigurationID || !product) return;

                var renderability = state.renderability[vehicleConfigurationID] || {};
                var partNumber = product.partNumber;
                var renderable = partNumber in renderability && renderability[partNumber].Renderable;

                if (!renderable) {
                    state.showError({
                        key: 'product-mount',
                        header: language["title-issue-visualizing-error"],
                        text: language["message-issue-visualizing-error"],
                        showClose: true
                    })
                }
            };

            event.listen(EVENT_PRODUCT_SELECTED, function (/** @type {RideStylerIntegrationProduct} */ product) {
                state.hideError('product-mount');

                if (ready.state() === 'resolved') checkVehicleProductMount();

                productSelected.resolve();
            });

            event.listen(EVENT_VEHICLE_INITIALIZED, function () {
                if (renderabilityLoaded.state() === 'resolved') {
                    state.populateProductRenderabilityStatus();
                }
                
                vehicleSelected.resolve();

                if ('paintColor' in previousState) {
                    event.broadcast(EVENT_PAINT_CHANGED, state.paintColor);
                }

                if ('suspension' in previousState) {
                    event.broadcast(EVENT_SUSPENSION_CHANGED, state.suspension);
                }

                if ('tireDiameter' in previousState) {
                    event.broadcast(EVENT_TIRE_DIAMETER_CHANGED, state.tireDiameter);
                }
            });

            ready.done(function () {
                checkVehicleProductMount();

                event.listen(EVENT_VEHICLE_INITIALIZED, function () {
                });
            
                event.listen(EVENT_PRODUCTS_INITIALIZED, function () {
                    state.loadProductRenderabilityStatus();
                });
            });

            $.when(vehicleOptionsLoaded, settingsLoaded).done(function (vehicles) {
                var usePreviousStateData = false;

                // Go through the matched vehicles for the search and see if we're still using
                // the last vehicle selected
                for (var i = 0; i < vehicles.length; i++) {
                    var vehicle = vehicles[i];
                    
                    if (vehicle.ConfigurationID === previousState.vehicleConfigurationID) {
                        usePreviousStateData = true;
                        break;
                    }
                }

                if (usePreviousStateData) {
                    state.paintColor = previousState.paintColor;
                    state.suspension = previousState.suspension;
                    state.lastUserSelectedSize = previousState.lastUserSelectedSize;
                    state.visualizingSideView = previousState.visualizingSideView;
                    state.tireDiameter = previousState.tireDiameter;
                    state.acceptedViewportDisclaimers = previousState.acceptedViewportDisclaimers || [];
                } else {
                    localStorage.removeItem('ridestylerIntegrationState');
                    previousState = {};
                }

                if (vehicles.length === 0) {
                    state.showError();
                } else if ('vehicleConfigurationID' in previousState) {
                    state.setVehicle(previousState.vehicleConfigurationID);
                } else if (vehicles.length > 1) {
                    state.change(STATE_SELECT_VEHICLE);
                } else {
                    state.setVehicle(vehicles[0].ConfigurationID);
                }

                state.loadProductRenderabilityStatus().done(function () {
                    renderabilityLoaded.resolve();
                });
            });

            state.setProducts(options.products);

            $.when(renderabilityLoaded, vehicleSelected).done(function () {
                var productGroups = state.validProductGroups();
    
                if (productGroups.length > 0) {
                    if ('activeProduct' in previousState) {
                        state.selectPartNumber(previousState.activeProduct) || state.selectProductGroup(productGroups[0]);
                    } else {
                        state.selectProductGroup(productGroups[0]);
                    }                
                } else {
                    console.error("No products to initialize the RideStyler integration with");
                    state.showError();
                }
            });
        },
        setupEverythingElse: function () {
            $container
                .on('click', 'button', function (e) {
                    e.preventDefault();
                })
                .on('click', '.rs-panel .rs-close', function () {
                    $(this).closest('.rs-panel').hide();
                })
                .on('click', function () {
                    $('.rs-tooltip', $container).removeClass('show');
                })
                .on('click', '.rs-help-icon', function (e) {
                    $('.rs-tooltip', this).toggleClass('show');

                    e.stopPropagation();
                })
                .on('click', '#rs-settings', function () {
                    $('#rs-mobile-settings-container', $container).addClass('show');
                })
                .on('click', '#rs-settings-apply-changes', function () {
                    $('#rs-mobile-settings-container', $container).removeClass('show');
                })
                .on('click', '#rs-error-refresh-button', function () {
                    location.reload();
                })
                .on('click', '#rs-error-close-button', function () {
                    state.hideError();
                })
                .on('click', '#rs-modal-close', function () {
                    $container.removeClass('modal-visible');

                    if (options.enableCompactMode)
                        $container.addClass('compact-mode');
                })
                .on('click', '#rs-settings-change-trim', function () {
                    state.change(STATE_SELECT_VEHICLE);
                })
                .on('click', '[disclaimer-accept]', function () {
                    var disclaimerSelector = $(this).attr('disclaimer-accept');

                    $(disclaimerSelector, $container).css('display', 'none');

                    state.viewportDisclaimerAccepted(disclaimerSelector, true);
                });

            var rsStateClassPrefix = 'rs-visualizer-state-';
            event.listen(EVENT_STATE_ENTER, function (newState) {
                var bodyClasses = document.body.className.split(/\s+/);
                var $body = $(document.body);
                
                // Remove previous state classes
                for (var i = 0; i < bodyClasses.length; i++) {
                    var bodyClass = bodyClasses[i];
                    
                    if (bodyClass.substr(0, rsStateClassPrefix.length) === rsStateClassPrefix) {
                        $body.removeClass(bodyClass);
                    }
                }

                $body.addClass(rsStateClassPrefix + newState);
            });

            event.listenStateEnter(STATE_NO_VEHICLE, function () {
                $('.rs-no-vehicle-overlay', $container).addClass('show');
            });

            event.listenStateEnter(STATE_SELECT_VEHICLE, function () {
                $('#rs-mobile-settings-container', $container).removeClass('show');
            });

            event.listenStateExit(STATE_INITIALIZING, function () {
                $container.removeClass('rs-initializing');
            });

            if (options.enableCompactMode) {
                $container.addClass('compact-mode-enabled compact-mode');
            }

            $container.find('.rs-viewport-disclaimer').css('display', 'none');
        },
        setupGallery: function () {
            if (options.hideProductGallery === true) {
                $('.rs-product-gallery', $container).css('visibility', 'hidden');
            }

            if (options.hidePaginationButtons === true) {
                $('.rs-product-gallery-pager,#rs-mobile-next,#rs-mobile-prev', $container).hide();
            }

            $container
                .on('click', '.rs-product-gallery-product', function () {
                    var $tile = $(this);

                    /** @type {RideStylerIntegrationProductGroup} */
                    var productGroup = $tile.data('productGroup');

                    state.selectProductGroup(productGroup);
                })
                .on('click', '.rs-product-gallery-pager', function () {
                    var $pagerButton = $(this);
                    var $productContainer = $pagerButton.siblings('.rs-product-gallery-product-container');
                    var scrollAmount = $productContainer.innerWidth()  * 0.5 * ($pagerButton.hasClass('rs-product-gallery-pager-next') ? 1 : -1);
                    var currentScroll = $productContainer.scrollLeft();

                    $productContainer.scrollLeft(currentScroll + scrollAmount);
                })
                .on('click', '#rs-mobile-next,#rs-mobile-prev', function (_e) {
                    /** @type {HTMLButtonElement} */
                    var button = this;
                    var isNext = button.id.substr(-4) === 'next';

                    if (isNext) state.selectNextProductGroup();
                    else state.selectPreviousProductGroup();
                });

            event.listen(EVENT_PRODUCTS_UPDATED, function (/** @type {RideStylerIntegrationProductGroup[]} */ productGroups) {
                var $gallery = $('.rs-product-gallery', $container);
                var $productContainer = $gallery.children('.rs-product-gallery-product-container');
                var $loadingIndicator = $gallery.children('.rs-loader');

                $loadingIndicator.hide();
                $productContainer.html($.map(productGroups, function (productGroup) {
                    var $tile = elementFactory.createGalleryProduct(productGroup);
                    var renderable = helpers.productGroupIsRenderable(productGroup);

                    if (!renderable) return undefined;

                    if (productGroup === state.activeProductGroup) {
                        $tile.addClass('active');
                    }

                    return $tile;
                }));
            });

            event.listen(EVENT_PRODUCT_GROUP_SELECTED, function (/** @type {RideStylerIntegrationProductGroup} */ productGroup) {
                var $gallery = $('.rs-product-gallery', $container).children('.rs-product-gallery-product-container');

                // Set the active class on the correct gallery element
                $gallery.children().removeClass('active').each(function () {
                    var $tile = $(this);

                    /** @type {RideStylerIntegrationProductGroup} */
                    var thisProductGroup = $tile.data('productGroup');

                    if (!thisProductGroup) return; // continue

                    if (helpers.compareProductGroups(productGroup, thisProductGroup)) {
                        // This is the active gallery tile
                        $tile.addClass('active');

                        return false; // break;
                    }
                });


            });
        },
        setupHeader: function () {
            // Vehicle Title
            $('.rs-vehicle-title', $container).text(options.vehicleDescription);

            if (options.hideVehicleDescription) {
                $('.rs-vehicle-title', $container).hide();
            }

            if (options.hideMoreDetails === true) {
                $('.rs-more-details', $container).hide();
            }

            if (options.productDisclaimer) {
                $('#rs-product-disclaimer .rs-tooltip-body', $container).text(options.productDisclaimer);
            } else {
                $('#rs-product-disclaimer', $container).closest('.rs-help-icon').hide();
            }

            $container
                .on('click', '.rs-more-details', function () {
                    var moreDetailsURL = state.activeProduct.moreDetailsURL;

                    if (options.moreDetailsRedirects === true) {
                        window.location.href = moreDetailsURL;
                    } else {
                        window.open(moreDetailsURL, "_blank");
                    }

                });

            event.listen(EVENT_PRODUCT_GROUP_SELECTED, function (/** @type {RideStylerIntegrationProductGroup} */ productGroup) {
                var $productHeader = $('.rs-product-header', $container);
                
                $productHeader
                    .children()
                        // Product Title
                        .filter('.rs-product-title').text(productGroup.title).end()
                        // Brand Logo
                        .filter('.rs-product-logo')
                            .prop('src', productGroup.brandLogoURL)
                            [productGroup.brandLogoURL ? 'show' : 'hide']()
                            .end();
            });

            event.listen(EVENT_PRODUCT_SELECTED, function (/** @type {RideStylerIntegrationProduct} */ product) {
                var $productHeader = $('.rs-product-header', $container);
                
                $productHeader
                    // Product Subtitle
                    .children('.rs-product-subtitle').text(product.fullDescription);

                // More Details
                {
                    var showMoreDetailsButton = options.hideMoreDetails !== true && !!product.moreDetailsURL;

                    $('.rs-more-details', $container)[showMoreDetailsButton ? 'show' : 'hide']();
                }
            });
        },
        setupPaint: function () {
            if (options.enableCompactPaint === true) {
                var updatePaintTop = function () {
                    var $panel = $container.find('#rs-paint-panel');
                    var $button = $('#rs-change-color', $container);
                    var buttonTop = $button.position().top;
                    var buttonHeight = $button.outerHeight();
                    var panelHeight = $panel.outerHeight();
                    var newTop = (buttonTop + (buttonHeight / 2) - panelHeight / 2).toFixed(2) + 'px';

                    $panel.css('top', newTop);
                };

                $container.find('#rs-paint-panel')
                    .addClass('compact rs-tooltip')
                    .on('rs.panel-shown', updatePaintTop)
                    .on('click', '.rs-paint-swatch', function (e) {
                        // Let the UI update, then hide the menu
                        setTimeout(function () {
                            var $panel = $(e.delegateTarget);

                            if ($panel.is(':visible')) {
                                $panel.hide().trigger('rs.panel-hidden');
                            }
                        }, 10);
                    });

                event.listen(EVENT_EXPANDED_CONTRACTED, updatePaintTop);
            }

            event.listen(EVENT_VEHICLE_INITIALIZED, function (/** @type {string} */ vehicleConfigurationID) {
                ridestyler.ajax.send({
                    action: 'vehicle/getpaintschemedescriptions',
                    data: {
                        VehicleConfiguration: vehicleConfigurationID
                    },
                    callback: function (response) {
                        $('#rs-paint-panel .rs-panel-body', $container)
                            .html($.map(response.Schemes, elementFactory.createPaintSwatch));
                    }
                })
            });

            event.listen(EVENT_PAINT_CHANGED, function (paintColor) {
                $('#rs-paint-panel .rs-paint-swatch', $container).each(function () {
                    var $this = $(this);
                    
                    $this.toggleClass('active', paintColor === $this.data('color'));
                });
            });

            $container
                .on('click', '.rs-paint-swatch', function () {
                    state.setPaintColor($.data(this, 'color'));
                });
        },
        setupTireSizeAdjustments: function () {
            var tireDiameterAdjustments = [31,33,35,37];

            if (!options.enableTireSizeAdjustments) return;
            if ($.isArray(options.enableTireSizeAdjustments)) tireDiameterAdjustments = options.enableTireSizeAdjustments;

            var $panelBody = $('#rs-tire-size-panel .rs-panel-body', $container);

            $panelBody.children(".rs-tire-size-adjustments-options").html($('<div/>').html($.map(tireDiameterAdjustments, elementFactory.createTireSizeButton)));

            var updateVisualizerMessages = helpers.debounce(function () {
                var adjustmentsActive = state.tireSizeAdjustments.active();
                var disclaimerSelector = '#rs-tire-size-adjustment-disclaimer';
                
                state.setVisualizerMessage('tire-diameter-set', adjustmentsActive && state.viewportDisclaimerAccepted(disclaimerSelector) && language['tire-size-adjustments-warning']);

                if (adjustmentsActive) helpers.showViewportDisclaimer(disclaimerSelector, true);
            }, 10);

            var update = helpers.debounce(function () {
                var showAngleUnavailable = !state.visualizingSideView;
                var showNeedsLift = !showAngleUnavailable && state.suspension < TIRE_SIZE_ADJUSTMENTS_MIN_VEHICLE_LIFT;
                var showOptions = !showAngleUnavailable && !showNeedsLift;
                var sectionVisibility = {
                    'rs-tire-size-adjustments-angle-unavailable': showAngleUnavailable,
                    'rs-tire-size-adjustments-needs-lift': showNeedsLift,
                    'rs-tire-size-adjustments-options': showOptions
                };

                $panelBody.children().each(function (_, child) {
                    var $child = $(child);

                    for (var className in sectionVisibility) {
                        if ($child.hasClass(className)) 
                            $child[sectionVisibility[className] ? 'show' : 'hide']();
                    }
                });

                if (state.tireDiameter && !state.tireSizeAdjustments.active())
                    state.setTireDiameter(undefined);

                updateVisualizerMessages();
            }, 10);

            event.listen(EVENT_VEHICLE_INITIALIZED, function () {
                var vehicleDescription = state.getCurrentVehicleOption();

                if (!vehicleDescription.HasSideImage)
                    $('.rs-tire-size-adjustments-change-to-side-view', $panelBody).hide();

                update();
            });
            event.listen(EVENT_SUSPENSION_CHANGED, update);
            event.listen(EVENT_VEHICLE_VIEW_CHANGED, update);
            event.listen(EVENT_DISCLAIMER_ACCEPTED, updateVisualizerMessages);

            $container
                .on('click', '.rs-tire-size-adjustments-change-to-side-view', function () {
                    state.toggleVehicleView();
                })
                .on('click', '.rs-tire-size-adjustments-options button', function () {
                    var tireSize = $.data(this, 'tireSize');

                    // If set, clear the tire diameter, otherwise set it
                    state.setTireDiameter(state.tireDiameter === tireSize ? undefined : tireSize);
                });
            
            event.listen(EVENT_TIRE_DIAMETER_CHANGED, function (tireSize) { 
                $('#rs-tire-size-panel .rs-tire-size-adjustments-options button', $container).each(function () {
                    var $this = $(this);
                    
                    $this.toggleClass('active', tireSize === $this.data('tireSize'));
                });

                updateVisualizerMessages();
            });
        },
        setupResponsiveness: function () {
            var $suspensionPanel = $('#rs-suspension-panel', $container);
            var suspensionRange = $('input', $suspensionPanel).data('plugin_jRange');

            var updateSliderWidth = function () {
                suspensionRange.options.width = $suspensionPanel.find('.rs-panel-body').innerWidth();
                suspensionRange.updateWidth();
                suspensionRange.setValue(suspensionRange.getValue());
            };

            $suspensionPanel.on('rs.panel-shown', updateSliderWidth);

            event.listen(EVENT_EXPANDED_CONTRACTED, updateSliderWidth);
            $(window).on('resize', updateSliderWidth);
        },
        setupSizeButtons: function () {
            if (options.hideSizes === true) {
                $('.rs-size-buttons', $container).hide();
            }

            $container
                .on('click', '.rs-size-buttons button', function () {
                    /** @type {RideStylerIntegrationProduct} */
                    var product = $(this).data('product');

                    state.lastUserSelectedSize = product.size;
                    state.selectProduct(product);
                });
            
            event.listen(EVENT_PRODUCT_GROUP_SELECTED, function (/** @type {RideStylerIntegrationProductGroup} */ productGroup) {
                var $sizeButtonContainer = $('.rs-size-buttons', $container);
                var oeSize = state.oeSize;
                var products = productGroup.products;

                products.sort(function (a,b) {
                    var aSize = a.size;
                    var bSize = b.size;
                    var aIsOE = aSize === oeSize;
                    var bIsOE = bSize === oeSize;

                    // Sort OE to the top
                    {
                        if (aIsOE && bIsOE) return 0;
                        if (aIsOE) return -1;
                        if (bIsOE) return 1;
                    }

                    // Then sort by string
                    {
                        if (aSize < bSize) return -1;
                        if (bSize < aSize) return 1;
                    }

                    return 0;
                });

                $sizeButtonContainer.empty();

                for (var i = 0; i < products.length; i++) {
                    var product = products[i];
                    var button = elementFactory.createSizeButton(product);

                    $sizeButtonContainer.append(button);
                }
            });

            event.listen(EVENT_PRODUCT_SELECTED, function (/** @type {RideStylerIntegrationProduct} */ product) {
                $('.rs-size-buttons', $container)
                    .children().each(function () {
                        var $sizeButton = $(this);

                        /** @type {RideStylerIntegrationProduct} */
                        var sizeProduct = $sizeButton.data('product');

                        $sizeButton.toggleClass('active', product === sizeProduct);
                    });
            });

        },
        setupSuspension: function () {
            /**
             * @param {RideStylerSuspensionRange} range 
             */
            var updateRange = function (range) {
                var jRange = $('#rs-suspension-panel input', $container).data('plugin_jRange');

                var min = range.Min;
                var max = range.Max;
                var step = 0.2;
                var labelStep = 1;
                var scale = [];

                var numberOfSteps = (max - min) / step;
                var stepsPerLabel = labelStep / step;
    
                for (var i = 0; i <= numberOfSteps; i++) {
                    if (i % stepsPerLabel === 0) {
                        scale.push((min + (i * step)).toFixed(1) + 'in');
                    } else {
                        scale.push('');
                    }
                }

                jRange.updateRange(min, max, step, scale);
            };

            $('#rs-suspension-panel input', $container)
                .on('change', function () {
                    state.setSuspension(this.value);
                })
                .jRange({
                    snap: true,
                    theme: 'rs-slider',
                    value: state.suspension
                });

            event.listen(EVENT_SUSPENSION_CHANGED, function (suspension) {
                var jRange = $('#rs-suspension-panel input', $container).data('plugin_jRange');

                jRange.setValue(suspension);
            });

            var suspensionOverrides = options.suspensionOverrides ? [].concat(options.suspensionOverrides, defaultSuspensionOverrides) : defaultSuspensionOverrides;
            var sanitizeSuspensionOverride = function (string) { 
                if (!string) return undefined;

                if (typeof string !== 'string') string = string.toString();

                return string.trim().toLowerCase();
            };

            event.listen(EVENT_VEHICLE_INITIALIZED, function () {
                var vehicleDescription = state.getCurrentVehicleOption();

                if (!vehicleDescription) return;
                
                var suspensionKey = vehicleDescription.StyleType + vehicleDescription.DriveType;
                var suspensionRange = suspensionKey in settings.suspensionLimits ? settings.suspensionLimits[suspensionKey] : settings.suspensionLimits.Default;

                if (jQuery.isArray(suspensionOverrides)) {
                    for (var j = 0; j < suspensionOverrides.length; j++) {
                        var suspensionOverride = suspensionOverrides[j];
                        var suspensionOverrideMatches = true; // A suspension override without any keys will match all vehicles

                        var make = sanitizeSuspensionOverride(suspensionOverride.Make);
                        var model = sanitizeSuspensionOverride(suspensionOverride.Model);
                        var trim = sanitizeSuspensionOverride(suspensionOverride.Trim);
                        var year = sanitizeSuspensionOverride(suspensionOverride.Year);

                        if (make && vehicleDescription.MakeName.toLowerCase() !== make)  suspensionOverrideMatches = false;
                        if (model && vehicleDescription.ModelName.toLowerCase() !== model) suspensionOverrideMatches = false;
                        if (trim && vehicleDescription.TrimName.toLowerCase() !== trim) suspensionOverrideMatches = false;
                        if (year && vehicleDescription.Year != year) suspensionOverrideMatches = false;

                        if (suspensionOverrideMatches) {
                            suspensionRange = suspensionOverride;
                            break;
                        }
                    }
                }

                state.suspensionRange = suspensionRange;

                updateRange(suspensionRange);
            });
        },
        setupVehicleSelect: function () {
            event.listenStateEnter(STATE_SELECT_VEHICLE, function () {
                var $vehicleSelectOverlay = $('.rs-vehicle-select-overlay', $container);
                var $vehicleSelectOptions = $('.rs-vehicle-select-options', $vehicleSelectOverlay);

                $vehicleSelectOptions.html(elementFactory.createVehicleSelectionButtons(state.vehicleOptions));
            });

            $container
                .on('click', '.rs-vehicle-select-options button', function () {
                    var $vehicleSelectButton = $(this);
                    var vehicleConfigurationID = $vehicleSelectButton.data('vehicleConfigurationID');

                    if (!vehicleConfigurationID) return;

                    state.setVehicle(vehicleConfigurationID);
                });
        },
        setupViewOnVehicleButtons: function () {
            $(document.body).on('click', '.rs-visualizer-button', function () {
                integration.selectProduct($(this).data('part-number'));
            });
        },
        setupVisualizer: function () {
            var visualizer = state.visualizer = new RideStylerViewport('.rs-viewport', {
                state: {
                    PositionX: 1,
                    PositionY: 1
                }
            });

            event.listen(EVENT_VEHICLE_INITIALIZED, function (vehicleConfigurationID) {
                var vehicleOption = state.getCurrentVehicleOption();

                if (state.visualizingSideView && !vehicleOption.HasSideImage) state.visualizingSideView = false;
                else if (!state.visualizingSideView && !vehicleOption.HasAngledImage) state.visualizingSideView = true;

                visualizer.Update({
                    VehicleConfiguration: vehicleConfigurationID,
                    Type: state.visualizingSideView ? 'Side' : 'Angle'
                });
            });

            event.listen(EVENT_PRODUCT_SELECTED, function (/** @type {RideStylerIntegrationProduct} */ product) {
                visualizer.Update({
                    WheelPartNumber: product.partNumber
                });
            });

            event.listen(EVENT_EXPANDED_CONTRACTED, function () {
                visualizer.Update();
            });

            event.listen(EVENT_SUSPENSION_CHANGED, function (suspension) {
                visualizer.Update({
                    Suspension: suspension
                });
            });

            event.listen(EVENT_TIRE_DIAMETER_CHANGED, function (tireDiameter) {
                if (!state.tireSizeAdjustments.active()) {
                    visualizer.Update({
                        TireDiameter: undefined,
                        TireRenderFileType: undefined
                    })
                } else {
                    visualizer.Update({
                        TireDiameter: tireDiameter,
                        TireRenderFileType: 'Traction'
                    })
                }
            });

            event.listen(EVENT_PAINT_CHANGED, function (paintColor) {
                visualizer.Update({
                    PaintColor: paintColor
                });
            });

            event.listen(EVENT_VEHICLE_VIEW_CHANGED, function (visualizingSideView) {
                visualizer.Update({
                    Type: visualizingSideView ? 'Side' : 'Angle'
                });
            });

            event.listen(EVENT_VISUALIZER_MESSAGES_CHANGED, function (visualizerMessages) {
                $('.rs-viewport-status', $container).html($.map(visualizerMessages, elementFactory.createVisualizerMessage))
            });

            event.listenStateEnter(STATE_SELECT_VEHICLE, function () {
                if (state.vehicleOptions && state.vehicleOptions.length)
                    visualizer.Update({
                        VehicleConfiguration: state.vehicleOptions[0].ConfigurationID
                    });
            });
        },
        setupVisualizerButtons: function () {
            var updateChangeViewVisibility = function () {
                var vehicleOption = state.getCurrentVehicleOption();
                var product = state.activeProduct;

                var vehicleCanChangeView = vehicleOption.HasAngledImage && vehicleOption.HasSideImage;
                var productCanChangeView = !product || product.hasAngledImage && product.hasSideImage;

                $('#rs-change-view', $container)[vehicleCanChangeView && productCanChangeView ? 'show' : 'hide']();
            };


            event.listen(EVENT_AFTER_VEHICLE_INITIALIZED, function () {
                $('#rs-settings-change-trim', $container)[state.vehicleOptions.length > 1 ? 'show' : 'hide']();

                $('#rs-adjust-tire-size', $container).css('display', state.tireSizeAdjustments.enabled() ? '' : 'none');

                updateChangeViewVisibility();
            });

            event.listen(EVENT_PRODUCT_SELECTED, function (_product) {
                updateChangeViewVisibility();
            });

            if (options.modalOnlyMode) {
                options.hideExpand = true;
                $container.addClass('modal-only-mode');
                $(document.body).addClass('rs-visualizer-modal-only-mode');
            }

            if (options.hideExpand === true) {
                $('#rs-expand', $container).hide();
            }

            if (options.hideSuspension === true) {
                $('#rs-change-suspension', $container).hide();
            }

            if (options.hideButtonText === true) {
                $('.rs-buttons-right .rs-icon-button span', $container).addClass('screen-reader-text');
            }

            if (options.enableTireSizeAdjustments) {
                $('#rs-adjust-tire-size', $container).show();
            }

            if (options.enableSharing) {
                $('#rs-share', $container).show();
            }

            $container
                .on('click', function (e) {
                    /** @type {HTMLElement} */
                    var container = this;

                    var isExpanded = $container.hasClass('expanded');
                    var isModal = $container.hasClass('modal-visible');
                    
                    if (e.target !== container || (!isExpanded && !isModal)) return;

                    if (e.offsetX < 0 || e.offsetY < 0 || e.offsetX > container.offsetWidth || e.offsetY > container.offsetHeight) {
                        if (isExpanded) $('#rs-expand', $container).click();
                        if (isModal) $container.removeClass('modal-visible');
                    }
                })
                .on('click', '#rs-change-view', function () {
                    state.toggleVehicleView();
                })
                .on('click', '#rs-expand', function () {
                    var isExpanded = !$container.hasClass('expanded');

                    var $button = $(this);
                    var url = $button.find('.rs-icon').attr('src');

                    if (isExpanded) url = url.replace('expand.png', 'minimize.png');
                    else url = url.replace('minimize.png', 'expand.png');

                    $container.toggleClass('expanded', isExpanded);
                    $button
                        .find('img').prop('src', url).end()
                        .find('span').text(isExpanded ? 'Minimize' : 'Expand');
                    
                    event.broadcast(EVENT_EXPANDED_CONTRACTED, isExpanded);
                })
                .on('click', '#rs-change-color, #rs-change-suspension, #rs-adjust-tire-size', function () {
                    var $allPanels = $('.rs-panel', $container);
                    var $targetPanel;

                    switch (this.id) {
                        case 'rs-change-color': 
                            $targetPanel = $('#rs-paint-panel', $container);
                            break;
                        case 'rs-change-suspension': 
                            $targetPanel = $('#rs-suspension-panel', $container); 
                            break;
                        case 'rs-adjust-tire-size':
                            $targetPanel = $('#rs-tire-size-panel', $container);
                            break;
                    }

                    var $visiblePanels = $allPanels.filter(':visible');
                    
                    if ($targetPanel && $visiblePanels.index($targetPanel) < 0) {
                        $targetPanel.show();
                        $targetPanel.triggerHandler('rs.panel-shown');
                    }

                    $visiblePanels.hide();
                    $visiblePanels.trigger('rs.panel-hidden');
                })
                .on('click', '.rs-view-in-visualizer', function () {
                    $container.removeClass('compact-mode');
                })
                .on('click', '#rs-share', function () {
                    state.share();
                });
        }
    };

    var integration = {
        event: event,
        
        /**
         * @param {RideStylerIntegrationProduct[]} products 
         */
        setProducts: function (products)  {
            state.setProducts(products);

            if (state.productGroups.length > 0) 
                state.selectProductGroup(state.productGroups[0]);
        },
        /**
         * @param {RideStylerIntegrationProduct|string} product 
         */
        selectProduct: function (product) {
            if ($container.is(':visible')) {
                var $window = $(window);
                var containerTop = $container.offset().top;
                var containerBottom = containerTop + $container.outerHeight();
                var windowTop = $window.scrollTop();
                var windowBottom = windowTop + $window.innerHeight();

                if (containerBottom < windowTop || containerTop > windowBottom || containerTop < windowTop) {
                    $window.scrollTop($container.offset().top - 10);
                }
            }

            $container.addClass('modal-visible').removeClass('compact-mode');

            if (typeof product === 'string') state.selectPartNumber(product);
            else state.selectProduct(product);
        }
    };

    initialization.run();

    return integration;
}